import { UserInfo } from 'types/entities/UserInfo';
import { ISharedEquipmentEntry, ISharedUser } from 'types/IEquipmentEntry';
import { SharedItem } from 'services/api/shares';
import { AccessSharingRole } from 'types/entities/AccessSharingRole';

/**
 * Groups shared users from equipment list, filtering by user permissions
 * and organizing shared equipment and fleets by user.
 */
export function groupSharedUsers(
  equipmentList: ISharedEquipmentEntry[],
  me: UserInfo,
): ISharedUser[] {
  const userMap = new Map<string, ISharedUser>();

  for (const equipment of equipmentList) {
    const contexts = getShareContexts(equipment);
    for (const context of contexts) {
      const myRole = context.shareData?.sharedItemDetails?.myRole as
        | AccessSharingRole
        | undefined;
      if (!canManageShareRole(myRole)) continue;
      processSharedUsers(context, me, userMap);
    }
  }

  return Array.from(userMap.values()).sort(sortByDisplayName);
}

function canManageShareRole(role?: AccessSharingRole): boolean {
  return role === 'OWNER' || role === 'ADMIN';
}

/**
 * Extracts share contexts (shared data and users) from equipment.
 * Returns both equipment and fleet contexts when available.
 */
function getShareContexts(equipment: ISharedEquipmentEntry) {
  const contexts: {
    shareData: SharedItem;
    isFleetShare: boolean;
    sharedWith: any[];
  }[] = [];

  if (equipment.shareData) {
    contexts.push({
      shareData: equipment.shareData,
      isFleetShare: false,
      sharedWith: equipment.shareData.sharedWith || [],
    });
  }

  if (equipment.fleetData) {
    contexts.push({
      shareData: equipment.fleetData,
      isFleetShare: true,
      sharedWith: equipment.fleetData.sharedWith || [],
    });
  }

  return contexts;
}

/**
 * Processes shared users and adds them to the user map
 */
function processSharedUsers(
  shareContext: {
    shareData: SharedItem;
    isFleetShare: boolean;
    sharedWith: any[];
  },
  me: UserInfo,
  userMap: Map<string, ISharedUser>,
): void {
  const { shareData, isFleetShare, sharedWith } = shareContext;

  for (const user of sharedWith) {
    if (user.email === me.email) continue;

    const existingUser = userMap.get(user.email);
    if (existingUser) {
      addShareToUser(existingUser, shareData, isFleetShare);
    } else {
      const newUser = createSharedUser(user, shareData, isFleetShare);
      userMap.set(user.email, newUser);
    }
  }
}

/**
 * Creates a new shared user object
 */
function createSharedUser(
  user: any,
  shareData: SharedItem,
  isFleetShare: boolean,
): ISharedUser {
  const sharedUser: ISharedUser = {
    email: user.email,
    displayName: user.displayName,
    role: user.sharedRole,
    sharedEquipments: [],
    sharedFleets: [],
  };

  addShareToUser(sharedUser, shareData, isFleetShare);
  return sharedUser;
}

/**
 * Adds share data to the appropriate array (equipment or fleet)
 */
function addShareToUser(
  user: ISharedUser,
  shareData: SharedItem,
  isFleetShare: boolean,
): void {
  if (isFleetShare) {
    user.sharedFleets.push(shareData);
  } else {
    user.sharedEquipments.push(shareData);
  }
}

/**
 * Sorts users by display name
 */
function sortByDisplayName(a: ISharedUser, b: ISharedUser): number {
  return a.displayName.localeCompare(b.displayName);
}

/**
 * Sorts shared items by label
 */
export function sortByLabel(a: SharedItem, b: SharedItem): number {
  return a.sharedItemDetails.sharedItemLabel.localeCompare(
    b.sharedItemDetails.sharedItemLabel,
  );
}
